import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    Box,
    Slider,
    InputAdornment,
    CircularProgress,
    IconButton,
} from '@mui/material';
import {
    DirectionsCar as CarIcon,
    LocalGasStation as FuelIcon,
    Speed as MileageIcon,
    Close as CloseIcon,
    CheckCircle as SuccessIcon,
} from '@mui/icons-material';
import { bookingService } from '../services/bookingService';
import { Booking } from '../types/booking';
import { useAuthStore } from '../store/authStore';

interface QuickHandoverModalProps {
    open: boolean;
    booking?: Booking | null;
    bookingId?: string | null;
    onClose: () => void;
    onSuccess: (updatedBooking: Booking) => void;
}

const TUNISIAN_DEEP_BLUE = '#273B7D';
const TUNISIAN_ORANGE = '#FF690F';
const TUNISIAN_SKY_BLUE = '#499FDD';

const QuickHandoverModal: React.FC<QuickHandoverModalProps> = ({ open, booking: propBooking, bookingId, onClose, onSuccess }) => {
    const { t } = useTranslation();
    const { user, employee } = useAuthStore();
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [booking, setBooking] = useState<Booking | null>(null);
    const [mileage, setMileage] = useState<number | ''>('');
    const [fuelLevel, setFuelLevel] = useState<number>(100);
    const [notes, setNotes] = useState('');
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const loadBooking = async () => {
            if (!open) return;

            if (propBooking) {
                setBooking(propBooking);
                return;
            }

            if (bookingId) {
                setFetching(true);
                setBooking(null);
                try {
                    const data = await bookingService.getBooking(bookingId);
                    setBooking(data);
                } catch (err) {
                    console.error('Failed to fetch booking for handover:', err);
                    setError(t('pos.quickHandover.loadFailed', { defaultValue: 'Failed to load booking details' }));
                } finally {
                    setFetching(false);
                }
            }
        };

        loadBooking();
    }, [propBooking, bookingId, open]);

    useEffect(() => {
        if (booking && open) {
            // Initialize with current vehicle mileage if available
            if (typeof booking.vehicleId === 'object' && booking.vehicleId.mileage) {
                setMileage(booking.vehicleId.mileage);
            }
            setFuelLevel(100);
            setNotes('');
            setError(null);
        }
    }, [booking, open]);

    const handleHandover = async () => {
        if (!booking || (!user && !employee)) return;
        // Shown as the actor name in the booking history (same as BookingsPage)
        const handedOverBy = employee?.fullName || user?.businessName || user?.displayName || 'Staff';

        const mileageValue = mileage === '' ? 0 : mileage;
        if (mileageValue <= 0) {
            setError(t('pos.quickHandover.validMileageRequired', { defaultValue: 'Please enter a valid mileage' }));
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const updatedBooking = await bookingService.handoverVehicle(booking._id, {
                mileage: mileageValue,
                fuelLevel,
                notes,
                condition: 'good',
                handedOverBy,
            });

            onSuccess(updatedBooking);
            onClose();
        } catch (err: any) {
            console.error('Handover failed:', err);
            setError(err.response?.data?.message || t('pos.quickHandover.processFailed', { defaultValue: 'Failed to process handover. Please try again.' }));
        } finally {
            setLoading(false);
        }
    };

    if (!open) return null;

    const vehicleName = booking && typeof booking.vehicleId === 'object'
        ? `${booking.vehicleId.make} ${booking.vehicleId.model}`
        : t('pos.quickHandover.vehicleFallback', { defaultValue: 'the vehicle' });

    return (
        <Dialog 
            open={open} 
            onClose={loading ? undefined : onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 4,
                    overflow: 'hidden',
                    background: 'linear-gradient(to bottom, #FFFFFF 0%, #FAFAFA 100%)',
                }
            }}
        >
            <DialogTitle sx={{ 
                bgcolor: TUNISIAN_DEEP_BLUE, 
                color: 'white', 
                p: 3, 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center' 
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CarIcon />
                    <Typography variant="h6" fontWeight={700}>
                        {t('pos.quickHandover.title', { defaultValue: 'Quick Handover' })}
                    </Typography>
                </Box>
                {!loading && (
                    <IconButton size="small" onClick={onClose} sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'white' } }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                )}
            </DialogTitle>

            <DialogContent sx={{ p: 4, mt: 1 }}>
                {fetching ? (
                    <Box sx={{ py: 6, textAlign: 'center' }}>
                        <CircularProgress size={32} sx={{ color: TUNISIAN_DEEP_BLUE }} />
                        <Typography sx={{ mt: 2 }} color="text.secondary">
                            {t('pos.quickHandover.loadingDetails', { defaultValue: 'Loading booking details...' })}
                        </Typography>
                    </Box>
                ) : !booking ? (
                    <Box sx={{ py: 6, textAlign: 'center' }}>
                        <Typography color="error">
                            {error || t('pos.quickHandover.couldNotLoad', { defaultValue: 'Could not load booking details.' })}
                        </Typography>
                        <Button onClick={onClose} sx={{ mt: 2 }}>
                            {t('common.close', { defaultValue: 'Close' })}
                        </Button>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <Box sx={{ mb: 1 }}>
                            <Typography variant="body1" sx={{ color: '#374151', mb: 1 }}>
                                {t('pos.quickHandover.confirmingPrefix', { defaultValue: 'Confirming handover of' })}{' '}
                                <strong>{vehicleName}</strong>{' '}
                                {t('pos.quickHandover.confirmingTo', { defaultValue: 'to' })}{' '}
                                <strong>{booking.customer.name}</strong>.
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('pos.quickHandover.description', { defaultValue: 'Please record the vehicle state at the moment of handover.' })}
                            </Typography>
                        </Box>

                        {error && (
                            <Box sx={{ p: 2, bgcolor: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 2 }}>
                                <Typography variant="body2" color="error" fontWeight={600}>
                                    {error}
                                </Typography>
                            </Box>
                        )}

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {/* Mileage Input */}
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, color: TUNISIAN_SKY_BLUE }}>
                                    <MileageIcon fontSize="small" />
                                    <Typography variant="subtitle2" fontWeight={700}>
                                        {t('pos.quickHandover.currentMileage', { defaultValue: 'CURRENT MILEAGE (KM)' })}
                                    </Typography>
                                </Box>
                                <TextField
                                    fullWidth
                                    type="number"
                                    value={mileage}
                                    onChange={(e) => setMileage(e.target.value === '' ? '' : Number(e.target.value))}
                                    placeholder={t('pos.quickHandover.mileagePlaceholder', { defaultValue: 'e.g. 45000' })}
                                    InputProps={{
                                        endAdornment: <InputAdornment position="end">KM</InputAdornment>,
                                        sx: { borderRadius: 3, bgcolor: 'white' }
                                    }}
                                />
                            </Box>

                            {/* Fuel Level Slider */}
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, color: '#059669' }}>
                                    <FuelIcon fontSize="small" />
                                    <Typography variant="subtitle2" fontWeight={700}>
                                        {t('pos.quickHandover.fuelLevel', { defaultValue: 'FUEL LEVEL' })}
                                    </Typography>
                                </Box>
                                <Box sx={{ px: 2 }}>
                                    <Slider
                                        value={fuelLevel}
                                        onChange={(_, val) => setFuelLevel(val as number)}
                                        step={1}
                                        min={0}
                                        max={100}
                                        valueLabelDisplay="auto"
                                        sx={{
                                            color: fuelLevel > 20 ? '#5EBD81' : '#E34712',
                                            '& .MuiSlider-thumb': {
                                                width: 24,
                                                height: 24,
                                                backgroundColor: '#fff',
                                                border: '2px solid currentColor',
                                            },
                                            '& .MuiSlider-track': { height: 8, borderRadius: 4 },
                                            '& .MuiSlider-rail': { height: 8, borderRadius: 4, opacity: 0.2 },
                                        }}
                                    />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                            {t('pos.quickHandover.emptyFuel', { defaultValue: 'Empty' })}
                                        </Typography>
                                        <Typography variant="subtitle2" fontWeight={800} color={fuelLevel > 20 ? '#059669' : '#E34712'}>
                                            {fuelLevel}%
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                            {t('pos.quickHandover.fullFuel', { defaultValue: 'Full' })}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>

                            {/* Additional Notes */}
                            <Box>
                                <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 1 }}>
                                    {t('pos.quickHandover.additionalNotes', { defaultValue: 'ADDITIONAL NOTES' })}
                                </Typography>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={2}
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    placeholder={t('pos.quickHandover.notesPlaceholder', { defaultValue: 'Clean inside/out, minor scratches on bumper, etc.' })}
                                    InputProps={{
                                        sx: { borderRadius: 3, bgcolor: 'white' }
                                    }}
                                />
                            </Box>
                        </Box>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 3, bgcolor: '#F9FAFB', borderTop: '1px solid #E5E7EB' }}>
                <Button 
                    onClick={onClose} 
                    disabled={loading}
                    sx={{ color: '#4B5563', fontWeight: 600, px: 3 }}
                >
                    {t('pos.quickHandover.processLater', { defaultValue: 'Process Later' })}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleHandover}
                    disabled={loading || fetching || !booking}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SuccessIcon />}
                    sx={{
                        bgcolor: TUNISIAN_ORANGE,
                        '&:hover': { bgcolor: '#E55A0A' },
                        borderRadius: 2.5,
                        px: 4,
                        py: 1.2,
                        fontWeight: 700,
                        boxShadow: '0 4px 14px 0 rgba(255, 105, 15, 0.4)'
                    }}
                >
                    {loading
                        ? t('common.processing', { defaultValue: 'Processing...' })
                        : t('pos.quickHandover.completeHandover', { defaultValue: 'Complete Handover' })}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default QuickHandoverModal;
